// Comprehensive error types for the application

export enum ErrorCategory {
  NETWORK = 'network',
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  VALIDATION = 'validation',
  STORAGE = 'storage',
  DATABASE = 'database',
  BUSINESS_LOGIC = 'business_logic',
  EXTERNAL_API = 'external_api',
  UNKNOWN = 'unknown'
}

export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

export interface ErrorContext {
  userId?: string;
  sessionId?: string;
  userAgent?: string;
  url?: string;
  timestamp?: Date;
  additionalData?: Record<string, any>;
}

export interface RecoveryAction {
  type: 'retry' | 'redirect' | 'refresh' | 'contact_support' | 'manual_action';
  label: string;
  description?: string;
  action?: () => void | Promise<void>;
  url?: string;
  icon?: string;
}

export interface BaseError extends Error {
  code: string;
  category: ErrorCategory;
  severity: ErrorSeverity;
  userMessage: string;
  technicalMessage: string;
  recoverable: boolean;
  retryable: boolean;
  context?: ErrorContext;
  originalError?: Error;
  recoveryActions?: RecoveryAction[];
  metadata?: Record<string, any>;
}

// Specific error types
export interface NetworkError extends BaseError {
  category: ErrorCategory.NETWORK;
  statusCode?: number;
  endpoint?: string;
  method?: string;
  timeout?: boolean;
}

export interface AuthenticationError extends BaseError {
  category: ErrorCategory.AUTHENTICATION;
  authProvider?: string;
  expired?: boolean;
}

export interface AuthorizationError extends BaseError {
  category: ErrorCategory.AUTHORIZATION;
  requiredPermissions?: string[];
  userPermissions?: string[];
}

export interface ValidationError extends BaseError {
  category: ErrorCategory.VALIDATION;
  field?: string;
  validationRule?: string;
  expectedFormat?: string;
  actualValue?: any;
}

export interface StorageError extends BaseError {
  category: ErrorCategory.STORAGE;
  operation?: 'upload' | 'download' | 'delete' | 'move';
  fileName?: string;
  fileSize?: number;
  bucketName?: string;
}

export interface DatabaseError extends BaseError {
  category: ErrorCategory.DATABASE;
  table?: string;
  operation?: 'select' | 'insert' | 'update' | 'delete' | 'rpc';
  constraint?: string;
}

export interface BusinessLogicError extends BaseError {
  category: ErrorCategory.BUSINESS_LOGIC;
  businessRule?: string;
  currentState?: any;
  expectedState?: any;
}

export interface ExternalApiError extends BaseError {
  category: ErrorCategory.EXTERNAL_API;
  apiName?: string;
  endpoint?: string;
  statusCode?: number;
  apiErrorCode?: string;
}

// Union type for all specific error types
export type AppError = 
  | NetworkError 
  | AuthenticationError 
  | AuthorizationError 
  | ValidationError 
  | StorageError 
  | DatabaseError 
  | BusinessLogicError 
  | ExternalApiError 
  | BaseError;

// Error event for logging
export interface ErrorEvent {
  error: AppError;
  timestamp: Date;
  handled: boolean;
  resolution?: string;
  resolutionTime?: number;
  userAction?: string;
}

// Error handler interface
export interface ErrorHandler {
  canHandle(error: AppError): boolean;
  handle(error: AppError): Promise<ErrorHandlingResult>;
}

export interface ErrorHandlingResult {
  success: boolean;
  message?: string;
  redirectUrl?: string;
  shouldRetry?: boolean;
  retryAfter?: number;
  recoveryActions?: RecoveryAction[];
}
